import { z } from "zod";
import { PaperDefaults, PaperRef } from "./paper.js";
import { Version } from "./concurrent.js";
import { RoundingDirection } from "./rounding.js";
import { REFERRAL_MEMO_MAX, REFERRAL_MSG, ReferralPct } from "./referral.js";
import { Email } from "./auth.js";
import { PhoneLike, RegNo, Zip, toHalfDigits } from "./company.js";
import { formatPhone } from "./phone.js";
import { PaymentTerms } from "./invoice.js";
import { BillingMode } from "./closing.js";
import { InstallmentPlan } from "./paySchedule.js";
import { BillingPlan, BillingTiming } from "./installment.js";
import { PartnerVendorPayments } from "./vendorPayment.js";
import { GroupPaymentRow, UnpaidFigures } from "./payment.js";
import { GroupMargin } from "./purchase.js";
import { ScheduleBrief } from "./schedule.js";
import { PartnerCode } from "./partnerSearch.js";

// ---------------------------------------------------------------------------
// 顧客（縦串2a → 縦串9 028、docs/23。「得意先」は 027 で「顧客」に。会社ごとに言い換え可）。
// docs/17 §2：顧客 ＞ 案件 ＞ 工事 ＞ 見積。
// 会社名・略称・フリガナ・法人番号・登録番号は平文（検索する・公開情報）、
// 会社の電話・FAX・住所・社内メモ・担当者の氏名・携帯・メール・支店の住所は暗号化（CLAUDE.md ルール5）
// ---------------------------------------------------------------------------

/** {customer} はその会社の言葉（既定「顧客」）に置き換わる（fillLabels。画面は t()） */
export const PARTNER_MSG = {
  nameRequired: "{customer}の名前を入れてください（会社名、または個人のお名前）",
  nameTooLong: "{customer}の名前は200文字までです",
  duplicate: "同じ名前の{customer}がすでにあります",
  notFound: "その{customer}は見つかりません",
  phoneInvalid: "電話番号は数字とハイフンで入れてください",
  shortNameRequired: "略称を入れてください（「株式会社」などを外した短い名前）",
  shortNameTooLong: "略称は50文字までです",
  kanaTooLong: "フリガナは100文字までです",
  corporateNumberInvalid: "法人番号は13桁の数字です（例：1234567890123）",
  regNoInvalid: "登録番号は T＋13桁の数字です（例：T1234567890123）",
  memoTooLong: "社内メモは2000文字までです",
  archived: "この{customer}は「使わない」になっています。戻してから使ってください",
  branchNameRequired: "支店・営業所の名前を入れてください（例：大阪支店）",
  branchNameTooLong: "支店・営業所の名前は100文字までです",
  branchDuplicate: "同じ名前の支店・営業所がすでにあります",
  branchNotFound: "その支店・営業所は見つかりません",
  branchClientMismatch: "その支店・営業所は別の{customer}のものです",
  contactNameRequired: "担当者の名前を入れてください",
  contactNotFound: "その担当者は見つかりません",
  contactClientMismatch: "その担当者は別の{customer}の人です",
  regNoRequired: "登録番号（T＋13桁）を入れてから「番号チェック」を押してください。法人番号が入っていれば T を付けて入れます",
  /** 保存のときの自動の番号チェックが国税庁に届かなかった（顧客の保存はできている。docs/23 §4） */
  autoCheckFailed: "番号チェックはできませんでした。",
  /** その通知のボタン（もう一度照会する） */
  autoCheckRetry: "再試行",
} as const;

/** 法人は御中、個人は様（013 partners.partner_kind / honorific） */
export const PartnerKind = z.enum(["corporate", "individual"]);
export type PartnerKind = z.infer<typeof PartnerKind>;

/** 敬称（013 の CHECK と同じ4つ。「殿」は官公庁向け、空は「なし」） */
export const Honorific = z.enum(["御中", "様", "殿", ""]);
export type Honorific = z.infer<typeof Honorific>;
export const HONORIFIC_LABEL: Record<Honorific, string> = { 御中: "御中", 様: "様", 殿: "殿", "": "なし" };
export const defaultHonorific = (kind: PartnerKind): Honorific => (kind === "individual" ? "様" : "御中");

export const Phone = z
  .string()
  .trim()
  .max(20, PARTNER_MSG.phoneInvalid)
  .regex(/^[0-9０-９\-－()（）+ ]*$/, PARTNER_MSG.phoneInvalid)
  .transform((s) => formatPhone(s));

/** 法人番号：空か13桁（全角も半角に）。公開情報なので平文 */
export const CorporateNumber = z
  .string()
  .trim()
  .transform((s) => toHalfDigits(s).replace(/[\s\-]/g, ""))
  .pipe(z.union([z.literal(""), z.string().regex(/^[0-9]{13}$/, PARTNER_MSG.corporateNumberInvalid)]));

/** 振込手数料の負担（028）。customer＝先方（既定。払う側が負担＝民法485条の原則）／us＝当方 */
export const TransferFeeBearer = z.enum(["customer", "us"]);
export type TransferFeeBearer = z.infer<typeof TransferFeeBearer>;
export const TRANSFER_FEE_LABEL: Record<TransferFeeBearer, string> = { customer: "先方（お客様）", us: "当方（自社）" };
/** 先方なら請求書を起こすとき備考に入る一文（Board と同じ文） */
export const TRANSFER_FEE_NOTE = "お振込手数料はご負担願います。";

/** 番号チェック（国税庁 適格請求書発行事業者公表 Web-API）の結果 */
export const QualifiedStatus = z.enum(["registered", "revoked", "expired", "not_found"]);
export type QualifiedStatus = z.infer<typeof QualifiedStatus>;
export const QUALIFIED_STATUS_LABEL: Record<QualifiedStatus, string> = {
  registered: "該当（適格請求書発行事業者）",
  revoked: "取消（登録は取り消されています）",
  expired: "失効（登録は失効しています）",
  not_found: "見つからない（この番号は公表されていません）",
};

/** 略称が空のときの表示名：「株式会社」「(株)」などを外す。何も残らなければ名前のまま */
const CORP_WORDS = ["株式会社", "有限会社", "合同会社", "合資会社", "合名会社", "一般社団法人", "一般財団法人", "公益社団法人", "公益財団法人", "医療法人", "社会福祉法人", "学校法人", "特定非営利活動法人", "NPO法人", "(株)", "（株）", "㈱", "(有)", "（有）", "㈲", "(同)", "（同）"];
export function stripCorporateWords(name: string): string {
  let s = name.trim();
  for (const w of CORP_WORDS) {
    if (s.startsWith(w)) s = s.slice(w.length);
    if (s.endsWith(w)) s = s.slice(0, -w.length);
  }
  s = s.trim();
  return s || name.trim();
}
export const displayNameOf = (p: { name: string; shortName: string }): string => p.shortName.trim() || stripCorporateWords(p.name);
/**
 * 選ぶ欄・並べた一覧の名前（#149）：略称。同じ略称が2社以上あるときだけ「略称（正式名）」。並びは渡した順のまま
 *   short は displayNameOf の値、full は正式名
 */
export function pickNamesOf(list: ReadonlyArray<{ short: string; full: string }>): string[] {
  const n = new Map<string, number>();
  for (const x of list) n.set(x.short, (n.get(x.short) ?? 0) + 1);
  return list.map((x) => ((n.get(x.short) ?? 0) > 1 && x.full.trim() && x.full.trim() !== x.short ? `${x.short}（${x.full.trim()}）` : x.short));
}
/**
 * 同じ相手が何行も出る一覧（見積依頼の一覧・案件の節。#149）の名前：相手（id）ごとに1つに数えてから pickNamesOf。id → 名前
 *   同じ外注先への依頼が2通（再依頼など）あっても、略称が同じ「別の会社」が無ければ略称だけ
 */
export function pickNameMapOf(list: ReadonlyArray<{ id: string; short: string; full: string }>): Map<string, string> {
  const uniq = [...new Map(list.map((x) => [x.id, x] as const)).values()];
  const names = pickNamesOf(uniq);
  return new Map(uniq.map((x, i) => [x.id, names[i] ?? x.short] as const));
}
/**
 * 頭文字の丸の1字（#96、docs/36 §4）：法人の種類（株式会社・有限会社・一般社団法人 など）と空白を除いた最初の1字。
 * 「株式会社 マッシュ」→「マ」、「阪神設備工業 株式会社」→「阪」、「西岡 一郎」→「西」
 */
export function partnerInitial(name: string): string {
  const s = stripCorporateWords(name).replace(/[\s\u3000]/g, "");
  return Array.from(s)[0] ?? "";
}

// ---------------------------------------------------------------------------
// 担当者（複数、028）。氏名・携帯・メールは暗号化。部署・肩書は平文（schema.sql のまま）
// ---------------------------------------------------------------------------
export const PartnerContact = z.object({
  id: z.string().uuid(),
  name: z.string(),
  department: z.string(),
  title: z.string(),
  /** 携帯（partner_contacts.phone_enc。会社の電話は顧客側） */
  mobile: z.string(),
  email: z.string(),
  isPrimary: z.boolean(),
  /** 支店付きなら支店の id。会社付きは null */
  branchId: z.string().uuid().nullable(),
});
export type PartnerContact = z.infer<typeof PartnerContact>;

export const PartnerContactInput = z.object({
  name: z.string().trim().min(1, PARTNER_MSG.contactNameRequired).max(100),
  department: z.string().trim().max(50).default(""),
  title: z.string().trim().max(50).default(""),
  mobile: Phone.default(""),
  email: z.union([Email, z.literal("")]).default(""),
  isPrimary: z.boolean().default(false),
  branchId: z.string().uuid().nullable().default(null),
  /** 版（#202）：画面が読んだときの Partner.updatedAt。渡すと、いまの版と違えば 409 PARTNER_STALE。省けば見ない（見積・{project}の作成画面のその場の登録） */
  version: Version.optional(),
});
export type PartnerContactInput = z.infer<typeof PartnerContactInput>;

// ---------------------------------------------------------------------------
// 支店・営業所（宛先、028 partner_branches）。支店名は平文、住所・電話・FAX は暗号化
// ---------------------------------------------------------------------------
export const PartnerBranch = z.object({
  id: z.string().uuid(),
  name: z.string(),
  postalCode: z.string(),
  address: z.string(),
  /** 住所の頭の都道府県のコード（1〜47、平文。030）。無ければ null */
  prefCode: z.number().int().min(1).max(47).nullable(),
  building: z.string(),
  phone: z.string(),
  fax: z.string(),
  sortOrder: z.number().int(),
  /** 支店コード（#288、docs/101 §3）。任意・取引先の中で一意。無ければ空 */
  branchCode: z.string().default(""),
});
export type PartnerBranch = z.infer<typeof PartnerBranch>;

export const PartnerBranchInput = z.object({
  name: z.string().trim().min(1, PARTNER_MSG.branchNameRequired).max(100, PARTNER_MSG.branchNameTooLong),
  postalCode: Zip.default(""),
  address: z.string().trim().max(200).default(""),
  building: z.string().trim().max(100).default(""),
  phone: PhoneLike.default(""),
  fax: PhoneLike.default(""),
  /** 支店コード（#288）。省けば変えない（新しい支店は無し） */
  branchCode: PartnerCode.optional(),
  /** 版（#202）。PartnerContactInput.version と同じ */
  version: Version.optional(),
});
export type PartnerBranchInput = z.infer<typeof PartnerBranchInput>;

export const PartnerBranchList = z.object({ items: z.array(PartnerBranch) });
export type PartnerBranchList = z.infer<typeof PartnerBranchList>;

// ---------------------------------------------------------------------------
// 顧客の登録（POST /partners。名前だけ必須。初期設定の壁を上げない＝ルール6.3）
// ---------------------------------------------------------------------------
/** 取引先の種別（partners.partner_type、047 で画面に）：顧客／外注先／両方（同じ会社が顧客でも外注先でもあるときは1件） */
export const PartnerType = z.enum(["client", "vendor", "both"]);
export type PartnerType = z.infer<typeof PartnerType>;
export const PARTNER_TYPE_LABEL: Record<PartnerType, string> = { client: "{customer}", vendor: "{vendor}", both: "{customer}・{vendor}の両方" };

export const PartnerCreate = z.object({
  name: z.string().trim().min(1, PARTNER_MSG.nameRequired).max(200, PARTNER_MSG.nameTooLong),
  /** 種別。既定は顧客。外注先の一覧から登録すると vendor */
  partnerType: PartnerType.default("client"),
  /** 略称（2026-09-23 必須に）。省くと名前から「株式会社」などを外して入れる（見積・案件の画面でその場で登録するとき） */
  shortName: z.string().trim().max(50, PARTNER_MSG.shortNameTooLong).optional(),
  kind: PartnerKind.default("corporate"),
  /** フリガナ・法人番号は「国税庁で検索」で入る（打ってもよい） */
  nameKana: z.string().trim().max(100, PARTNER_MSG.kanaTooLong).optional(),
  corporateNumber: CorporateNumber.optional(),
  /** 会社の電話（暗号化される。028 から顧客側） */
  phone: Phone.optional(),
  /** 担当者（暗号化される）。無くてもよい。携帯は担当者の携帯。部署・肩書は平文（2026-09-23 登録の画面でも入れられるように） */
  contactName: z.string().trim().max(100).optional(),
  contactDepartment: z.string().trim().max(50).optional(),
  contactTitle: z.string().trim().max(50).optional(),
  mobile: Phone.optional(),
  email: z.union([Email, z.literal("")]).optional(),
  /** 住所（025、暗号化される）。無くてもよい。郵便番号は空か7桁（「560-0081」「〒５６０-００８１」も可） */
  postalCode: Zip.optional(),
  address: z.string().trim().max(200).optional(),
  building: z.string().trim().max(100).optional(),
  /**
   * ここから下は 2026-09-23（射場さん：登録の画面も詳細と同じように入れたい）。省けば今までどおり。
   * 敬称は省けば kind から（法人＝御中、個人＝様）。支払条件は省けば既定（月末締め・翌月末払い）のまま
   */
  honorific: Honorific.optional(),
  fax: PhoneLike.optional(),
  invoiceRegistrationNo: RegNo.optional(),
  transferFeeBearer: TransferFeeBearer.optional(),
  memo: z.string().trim().max(2000, PARTNER_MSG.memoTooLong).optional(),
  paymentTerms: PaymentTerms.optional(),
  /** 顧客コード・仕入先コード（#288、docs/101 §3）。任意。全角は半角に直す。重複は 409 PARTNER_CODE_DUP */
  customerCode: PartnerCode.optional(),
  vendorCode: PartnerCode.optional(),
});
export type PartnerCreate = z.infer<typeof PartnerCreate>;

/**
 * 顧客の編集（PUT /partners/:id、詳細画面）。**省いた欄は変えない**。担当者・支店は別の API。
 * honorific を省くと kind から（法人＝御中、個人＝様）
 */
export const PartnerUpdate = z.object({
  partnerType: PartnerType.optional(),
  name: z.string().trim().min(1, PARTNER_MSG.nameRequired).max(200, PARTNER_MSG.nameTooLong).optional(),
  /** 略称は必須（2026-09-23）。空にはできない */
  shortName: z.string().trim().min(1, PARTNER_MSG.shortNameRequired).max(50, PARTNER_MSG.shortNameTooLong).optional(),
  nameKana: z.string().trim().max(100, PARTNER_MSG.kanaTooLong).optional(),
  kind: PartnerKind.optional(),
  honorific: Honorific.optional(),
  phone: PhoneLike.optional(),
  fax: PhoneLike.optional(),
  postalCode: Zip.optional(),
  address: z.string().trim().max(200).optional(),
  building: z.string().trim().max(100).optional(),
  corporateNumber: CorporateNumber.optional(),
  invoiceRegistrationNo: RegNo.optional(),
  transferFeeBearer: TransferFeeBearer.optional(),
  memo: z.string().trim().max(2000, PARTNER_MSG.memoTooLong).optional(),
  /**
   * パートナー（#213、docs/40 §3。モジュール referral の会社だけ＝OFF なら 404）。手数料は％（小数2桁・0 以上 90 未満。パートナー ON のときは 0 より大きく＝API が見る）。
   * 備考は社内用 20字（暗号化）。顧客・外注先と重なってよい
   */
  isReferrer: z.boolean().optional(),
  referralPct: ReferralPct.optional(),
  referralMemo: z.string().trim().max(REFERRAL_MEMO_MAX, REFERRAL_MSG.memoTooLong).optional(),
  /** 顧客コード・仕入先コード（#288）。空＝無し。重複は 409 PARTNER_CODE_DUP（相手の名前） */
  customerCode: PartnerCode.optional(),
  vendorCode: PartnerCode.optional(),
  /** 版（#202）：画面が読んだときの Partner.updatedAt。渡すと、いまの版と違えば 409 PARTNER_STALE（顧客の詳細の画面は必ず送る） */
  version: Version.optional(),
});
export type PartnerUpdate = z.infer<typeof PartnerUpdate>;

/** 顧客の小さな書き込み（使わない／戻す・担当者と支店の削除の本文）の版（#202）。省けば見ない */
export const PartnerVersion = z.object({ version: Version.optional() });
export type PartnerVersion = z.infer<typeof PartnerVersion>;
/** 支払条件（PUT /partners/:id/terms）。版つき（#202） */
export const PartnerTermsUpdate = PaymentTerms.extend({
  /** 請求の形（#267）。省けば変えない */
  billingMode: BillingMode.optional(),
  /** 分割払い（#267 3b）：null＝なし。省けば変えない */
  installments: InstallmentPlan.nullable().optional(),
  version: Version.optional(),
});
export type PartnerTermsUpdate = z.infer<typeof PartnerTermsUpdate>;
/** 書式（PUT /partners/:id/paper）。版つき（#202） */
export const PartnerPaperUpdate = PaperDefaults.extend({ version: Version.optional() });
export type PartnerPaperUpdate = z.infer<typeof PartnerPaperUpdate>;

/** 送信方法の既定（#303 B2、partners.send_method）：print＝印刷／mail＝メール */
export const PartnerSendMethod = z.enum(["print", "mail"]);
export type PartnerSendMethod = z.infer<typeof PartnerSendMethod>;
/** 送付状の既定（PUT /partners/:id/cover。#223 の2回目）。省いたキーは変えない。版つき（#202） */
export const PartnerCoverUpdate = z.object({
  templateId: z.string().uuid().nullable().optional(),
  window: z.boolean().optional(),
  /** メールの渡し方（#225 ★1）：page＝閲覧ページ／attach＝添付／null＝書類の既定（帳票は閲覧ページ・見積依頼書は添付） */
  mailMode: z.enum(["page", "attach"]).nullable().optional(),
  /** 送信方法の既定（#303 B2）：発行したあとの送信の画面がこの方法で開く（その場で変えられる） */
  sendMethod: PartnerSendMethod.optional(),
  /** 送付状（#303 B2）：true＝添付する／false＝添付しない／null＝会社に従う（設定 → 送付状 の 印刷に添付・メールに添付） */
  attach: z.boolean().nullable().optional(),
  version: Version.optional(),
});
export type PartnerCoverUpdate = z.infer<typeof PartnerCoverUpdate>;

/** 一覧の1行（GET /partners）。担当者は「主」の1人だけ */
export const PartnerSummary = z.object({
  id: z.string().uuid(),
  name: z.string(),
  /** 顧客／外注先／両方（047） */
  partnerType: PartnerType,
  /** 略称（空なら無し）。一覧・候補・ホームには displayName を使う */
  shortName: z.string(),
  displayName: z.string(),
  nameKana: z.string(),
  kind: PartnerKind,
  honorific: z.string(),
  /** 会社の電話・FAX（暗号化。無ければ空） */
  phone: z.string(),
  fax: z.string(),
  /** 住所（025、暗号化）。無ければ空。郵便番号は7桁（ハイフンなし） */
  postalCode: z.string(),
  address: z.string(),
  /** 住所の頭の都道府県のコード（1〜47、平文。030）。県別の集計に使う。無ければ null */
  prefCode: z.number().int().min(1).max(47).nullable(),
  building: z.string(),
  corporateNumber: z.string(),
  invoiceRegistrationNo: z.string(),
  transferFeeBearer: TransferFeeBearer,
  /** 主担当（無ければ null） */
  contact: PartnerContact.nullable(),
  /** 支店・営業所の数（使わないにしたものは除く） */
  branchCount: z.number().int(),
  /** 支払条件（締め日・支払月・支払日、024）。無ければ null＝既定（月末締め・翌月末払い）で請求書のお支払期限を出す */
  paymentTerms: z.object({ closingDay: z.number().int(), paymentMonthOffset: z.number().int(), paymentDay: z.number().int() }).nullable(),
  /** 「使わない」にした日時。一覧は既定で出さない（?archived=1 で出す） */
  archivedAt: z.string().nullable(),
  createdAt: z.string(),
  /** この{customer}の{project}の数（進行中・完了を含む。消した{project}は除く）。GET /partners?sort=used のときだけ（#96、docs/36 §4） */
  groupCount: z.number().int().optional(),
  /** パートナー（#213、docs/40 §3）と手数料の率（％。既定 0） */
  isReferrer: z.boolean(),
  referralPct: z.number(),
  /** 顧客コード・仕入先コード（#288、docs/101 §3）。無ければ空 */
  customerCode: z.string().default(""),
  vendorCode: z.string().default(""),
});
export type PartnerSummary = z.infer<typeof PartnerSummary>;

/** 詳細（GET /partners/:id）：一覧の行 ＋ 担当者全員・支店・番号チェックの結果・社内メモ */
export const Partner = PartnerSummary.extend({
  contacts: z.array(PartnerContact),
  branches: z.array(PartnerBranch),
  /** 法人番号から名称・住所を引いた日時（無ければ null） */
  corporateFetchedAt: z.string().nullable(),
  /** 紙の書式（#78、044・046）：この顧客に出す見積書・請求書・領収書の書式。null＝会社の既定に従う */
  paper: z.object({ quote: PaperRef.nullable(), invoice: PaperRef.nullable(), receipt: PaperRef.nullable(), order: PaperRef.nullable().default(null) }),
  /** 消費税の端数処理の向き（#108、docs/41 ★4）。null＝会社の設定に従う。変えるのは PUT /partners/:id/tax-rounding（管理者） */
  taxRounding: RoundingDirection.nullable(),
  /** 税を確定する書類（#266、docs/96 決めごと13）：invoice＝請求書（既定）／delivery＝納品書。変えるのは PUT /partners/:id/tax-on（管理者） */
  taxOn: z.enum(["invoice", "delivery"]).default("invoice"),
  /** 請求の形（#267、docs/97 決めごと1）：per_delivery＝都度（既定）／closing＝締め。変えるのは PUT /partners/:id/terms（支払条件と一緒に） */
  billingMode: BillingMode.default("per_delivery"),
  /** 分割払い（#267 3b、docs/97 決めごと10）：回数と間隔。null＝なし（既定）。変えるのは PUT /partners/:id/terms（支払条件と一緒に） */
  installments: InstallmentPlan.nullable().default(null),
  /** {acceptance}の既定（#266 1b、スイッチの2段目）：null＝会社に従う（使う機能「{acceptance}」のとおり）。変えるのは PUT /partners/:id/acceptance */
  acceptanceDefault: z.boolean().nullable().default(null),
  /**
   * 送付状の既定（#223 の2回目、docs/89 §6）：テンプレート（null＝会社の既定）と窓付封筒。紙の画面の送付状の枠・単体送付状の初めの値（会社の既定より先）。
   * 変えるのは PUT /partners/:id/cover（だれでも）。削除したテンプレートを指していれば templateId は null で返す（会社の既定に落ちる）
   */
  /** 送信設定（#223 の2回目・#225・#303 B2）：テンプレート・窓付封筒・メールの渡し方・送信方法の既定・送付状（null＝会社に従う） */
  cover: z.object({ templateId: z.string().uuid().nullable(), window: z.boolean(), mailMode: z.enum(["page", "attach"]).nullable(), sendMethod: PartnerSendMethod, attach: z.boolean().nullable() }),
  /** 番号チェックの結果（未確認なら null） */
  qualified: z
    .object({
      status: QualifiedStatus,
      registeredOn: z.string().nullable(),
      revokedOn: z.string().nullable(),
      checkedAt: z.string(),
      /** 国税庁に登録されている名称（顧客名と違えば画面で見くらべる） */
      ntaName: z.string().nullable(),
    })
    .nullable(),
  /** 社内メモ（暗号化。お客様には出ない） */
  memo: z.string(),
  /** パートナーの備考（#213。社内用 20字・暗号化） */
  referralMemo: z.string(),
  /**
   * 保存（POST /partners・PUT /partners/:id）の答えだけ：保存のときに番号チェックを自動で流したか（docs/23 §4、2026-10-13）。
   * checked＝流して結果を保存した（qualified に入っている）／failed＝国税庁に照会できなかった（顧客の保存はできている。結果は未確認のまま）／省く＝流さなかった
   */
  autoCheck: z.enum(["checked", "failed"]).optional(),
  /** 版（#202）：partners.updated_at（ミリ秒までの ISO）。保存のたびに version として送り返す */
  updatedAt: z.string(),
  /** 未払い（#233 3a。sales の会社で{vendor}・両方の取引先だけ）：確定した仕入の残の合計と、そのうち期限超過 */
  payable: UnpaidFigures.optional(),
  /** 支払の履歴と今月の支払（#233 3c。payable と同じ取引先だけ） */
  vendorPayments: PartnerVendorPayments.optional(),
});
export type Partner = z.infer<typeof Partner>;

export const PartnerList = z.object({ items: z.array(PartnerSummary) });
export type PartnerList = z.infer<typeof PartnerList>;

// ---------------------------------------------------------------------------
// 案件（現場）＝ project_groups（013・014・018）。顧客の下に「西岡本邸」のような箱。
// 現場住所・施主名は暗号化。見積を作るとき現場住所を見積に写す（打ち直しできる）
// ---------------------------------------------------------------------------

export const GROUP_MSG = {
  nameRequired: "{project}の名前を入れてください",
  nameTooLong: "{project}の名前は200文字までです",
  duplicate: "同じ名前の{project}がこの{customer}にすでにあります",
  notFound: "その{project}は見つかりません",
  clientMismatch: "その{project}は別の{customer}のものです",
  /* ---- 現場の地図（#132、docs/49） ---- */
  geoOutOfJapan: "ピンは日本の中に置いてください",
  geoNoAddress: "現場住所が入っていないので、住所から座標を取れません",
  geoFollowAsk: "住所が変わりました。ピンを新しい住所に合わせますか",
  /* ---- ピンの位置から住所を入れる（#135、docs/49 §11） ---- */
  geoNoPin: "ピンが無いので、住所を取れません",
  geoReverseNone: "この位置の住所を取れませんでした",
  geoReverseAsk: "ピンの位置の住所を現場住所に入れますか",
  geoReverseDone: "現場住所を入れました",
  geoTownOnly: "番地の位置データがありません。手動でマップ設定してください。",
  /* ---- 案件の画面と「見積追加」（縦串10、033、docs/25 → 039、docs/28） ---- */
  addJobNotIssued: "出したあとの見積からだけ、追加の見積を作れます",
  /** トーストの文。{name} は案件の名前に画面が置き換える */
  jobAdded: "{project}「{name}」に見積を追加しました",
  addJobNote: "「見積追加」は別の番号の見積になります（請求書も別）。同じ見積を修正して出し直すときは、その見積の「訂正版作成」から。",
  noJobs: "この{project}にはまだ見積がありません",
  /* ---- 案件を中心にする（039、docs/28） ---- */
  clientRequired: "{customer}を選んでください（無ければ名前を打つと、その場で登録できます）",
  notEmpty: "見積が入っている{project}は消せません",
  staffTooLong: "担当者は50文字までです",
  memoTooLong: "社内メモは10000文字までです",
  created: "{project}「{name}」を作りました。「見積作成」から最初の見積を作れます",
  saved: "{project}を保存しました。出した紙は変わりません。新しく作る見積に写ります",
  completed: "{project}「{name}」を完了にしました。一覧の「完了」に移ります",
  reopened: "{project}「{name}」を進行中に戻しました",
  deleted: "{project}「{name}」を消しました",
  /** #265（docs/81 §12）→ #271（§13）：ゴミ箱に見積書があるときの確認の窓（題・本文）。消すとゴミ箱の見積書も完全に削除＝一覧の通知に「元に戻す」を付けない */
  deleteTitle: "{project}削除",
  deleteWithTrash: "{project}「{name}」を削除します。ゴミ箱の見積書 {n}件も完全に削除され、復元できません",
  /** ゴミ箱に番号のある見積書があるとき（#271）。{numbers}＝番号（「、」区切り） */
  deleteWithTrashNumbered: "{project}「{name}」を削除します。ゴミ箱の見積書 {n}件（番号あり：{numbers}）も完全に削除され、復元できません",
  /** 9/29（#92）：後半（完了にすると…／見積を追加すると…）は案件の画面の「案件詳細」の「？」へ */
  allBilled: "この{project}はすべて請求済です。",
  isCompleted: "{date} に完了にした{project}です。",
  /** #145：案件名は出さない（新しい案件は名前がまだ「（件名なし）」のことがある） */
  estimateCreatedWithGroup: "見積の下書きを作りました。{project}も合わせて作成しました",
  estimateCreatedInGroup: "{project}「{name}」に見積の下書きを作りました",
  followNote: "この{project}の名前は、見積が1枚のあいだ{subject}に合わせて変わります",
} as const;

export const ProjectGroupCreate = z.object({
  name: z.string().trim().min(1, GROUP_MSG.nameRequired).max(200, GROUP_MSG.nameTooLong),
  /** 現場住所（暗号化される）。無くてもよい */
  siteAddress: z.string().trim().max(200).optional(),
  /** 施主名（暗号化される）。下請けで請求先（元請）と施主が違うとき */
  ownerName: z.string().trim().max(100).optional(),
});
export type ProjectGroupCreate = z.infer<typeof ProjectGroupCreate>;

export const ProjectGroup = z.object({
  id: z.string().uuid(),
  clientId: z.string().uuid(),
  name: z.string(),
  siteAddress: z.string().nullable(),
  ownerName: z.string().nullable(),
  /** 039：見積を作るときに写す 支店・顧客担当者・担当者 */
  partnerBranchId: z.string().uuid().nullable(),
  partnerContactId: z.string().uuid().nullable(),
  staffName: z.string(),
  /** 完了にした日時（039）。見積の紙のセレクトには、完了の案件は出さない（その見積が入っている案件は別） */
  completedAt: z.string().nullable(),
  /** 名前を工事名に合わせているか（039。自動でできた案件で、見積が1枚のあいだ） */
  nameFollowsSubject: z.boolean(),
  /** この案件に付いている見積の数（画面の「西岡本邸（見積 2件）」） */
  quoteCount: z.number().int(),
  /** 案件の集計（033 v_project_group_summary。顧客の詳細の右列用）。金額は税込 */
  jobCount: z.number().int(),
  orderedCount: z.number().int(),
  orderedTotal: z.number().int(),
  /** 継続案件（#289、docs/101 §5）と開始日 */
  isOngoing: z.boolean().default(false),
  startedOn: z.string().nullable().default(null),
});
export type ProjectGroup = z.infer<typeof ProjectGroup>;
export const ProjectGroupList = z.object({ items: z.array(ProjectGroup) });
export type ProjectGroupList = z.infer<typeof ProjectGroupList>;

// ---------------------------------------------------------------------------
// 案件の画面（縦串10、033、docs/25）。/groups/:id。
// 集計の決まりは /home の月の数字と同じ（受注＝ordered、金額＝出ている最新の版、利益はルール7）
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// 案件の状態（039、docs/28 決めごと11）：見積から自動で決める。手で付けるのは「完了」だけ
// ---------------------------------------------------------------------------

export const GroupStatus = z.enum(["none", "estimating", "ordered", "billed", "lost", "done"]);
export type GroupStatus = z.infer<typeof GroupStatus>;
export const GROUP_STATUS_LABEL: Record<GroupStatus, string> = {
  none: "見積なし",
  estimating: "見積中",
  ordered: "受注",
  billed: "請求済",
  lost: "失注",
  done: "完了",
};
/**
 * 案件の状態。完了にしていれば「完了」。見積が無ければ「見積なし」、すべて失注なら「失注」、
 * 受注があれば 請求していない受注があるうちは「受注」・全部請求したら「請求済み」、それ以外は「見積中」
 */
export function groupStatusOf(x: { jobCount: number; lostCount: number; orderedCount: number; unbilledCount: number; completedAt: string | null }): GroupStatus {
  if (x.completedAt) return "done";
  if (x.jobCount === 0) return "none";
  if (x.lostCount === x.jobCount) return "lost";
  if (x.orderedCount > 0) return x.unbilledCount > 0 ? "ordered" : "billed";
  return "estimating";
}
/** 一覧の「進行中」に入るか（完了と、すべて失注の案件は「完了」の側） */
export const isActiveGroupStatus = (st: GroupStatus): boolean => st !== "done" && st !== "lost";

/** 案件の支払条件（039）。source＝どこで決まったか（この案件だけ／顧客の支払条件／既定） */
export const GroupTerms = z.object({ terms: PaymentTerms, source: z.enum(["group", "client", "default"]) });
export type GroupTerms = z.infer<typeof GroupTerms>;

/** 案件の一覧の1行（GET /groups） */
export const GroupListItem = z.object({
  id: z.string().uuid(),
  name: z.string(),
  client: z.object({ id: z.string().uuid(), name: z.string(), honorific: z.string() }),
  branchName: z.string().nullable(),
  staffName: z.string(),
  jobCount: z.number().int(),
  /** 受注した見積（最新の版）の税込の合計と、その税抜（#94：一覧で税込の下に小さく） */
  ordered: z.object({ count: z.number().int(), total: z.number().int(), subtotal: z.number().int().default(0) }),
  billed: z.object({ count: z.number().int(), total: z.number().int() }),
  waitingCount: z.number().int(),
  draftCount: z.number().int(),
  status: GroupStatus,
  completedAt: z.string().nullable(),
  lastActivityAt: z.string(),
  /* ---- 継続案件（#289、docs/101 §7）：受注の代わりに「今月売上」「未請求」（税抜）、2行目に「開始 yyyy/mm/dd」 ---- */
  isOngoing: z.boolean().default(false),
  startedOn: z.string().nullable().default(null),
  monthSales: z.number().int().default(0),
  unbilled: z.number().int().default(0),
  /** 最後に書類を作った日（作成画面の欄の「最終 10/30」）。無ければ null */
  lastUsedOn: z.string().nullable().default(null),
  partnerBranchId: z.string().uuid().nullable().default(null),
  partnerContactId: z.string().uuid().nullable().default(null),
  quoteCount: z.number().int().default(0),
});
export type GroupListItem = z.infer<typeof GroupListItem>;
/** 一覧のタブ（#289 で ongoing＝継続。使う機能「{ongoingProject}」がオンの会社だけ出す） */
export const GroupListState = z.enum(["active", "ongoing", "done", "all"]);
export type GroupListState = z.infer<typeof GroupListState>;
export const GroupList = z.object({
  items: z.array(GroupListItem),
  /** 絞り込みのボタンの数（同じ担当者・検索の条件で数える） */
  counts: z.object({ active: z.number().int(), done: z.number().int(), all: z.number().int(), ongoing: z.number().int().default(0) }),
  /** 担当者の絞り込みの候補（この会社の案件にある担当者の名前） */
  staffNames: z.array(z.string()),
});
export type GroupList = z.infer<typeof GroupList>;

/** 案件を作る（POST /groups。サイドメニューの「新しい案件」）。必須は顧客と案件名 */
export const GroupCreate = z.object({
  clientId: z.string().uuid({ message: GROUP_MSG.clientRequired }),
  name: z.string().trim().min(1, GROUP_MSG.nameRequired).max(200, GROUP_MSG.nameTooLong),
  partnerBranchId: z.string().uuid().nullable().default(null),
  partnerContactId: z.string().uuid().nullable().default(null),
  staffName: z.string().trim().max(50, GROUP_MSG.staffTooLong).default(""),
  /** 支払条件の上書き。null なら顧客に従う */
  paymentTerms: PaymentTerms.nullable().default(null),
  memo: z.string().trim().max(10000, GROUP_MSG.memoTooLong).default(""),
  siteAddress: z.string().trim().max(200).default(""),
  ownerName: z.string().trim().max(100).default(""),
  billingTiming: BillingTiming.default("lump"),
  /** 継続案件（#289）。開始日は省けば今日（継続案件のときだけ） */
  isOngoing: z.boolean().optional(),
  startedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
});
export type GroupCreate = z.infer<typeof GroupCreate>;

/** 案件の中の1つの仕事（見積）。状態は一覧と同じ5つ */
export const GroupJob = z.object({
  id: z.string().uuid(),
  /** 表示用の番号（訂正版は枝番つき Q-0012-2。下書きは null） */
  displayNo: z.string().nullable(),
  subject: z.string(),
  status: z.enum(["draft", "waiting", "ordered", "billed", "lost"]),
  /** 出ている版の税込。下書きは書きかけの金額 */
  total: z.number().int(),
  /** 同じ版の税抜（案件の画面で税込の下に小さく。9/28 射場さん） */
  subtotal: z.number().int().default(0),
  issueDate: z.string(),
  /** この工事のいちばん新しい請求書（下書きも含む）。無ければ null */
  invoice: z.object({ id: z.string().uuid(), documentNo: z.string().nullable(), status: z.string() }).nullable(),
  /** 039：この見積の請求書すべて（無効は除く。古い順）。案件の画面で見積の下に並べる */
  invoices: z.array(
    z.object({ id: z.string().uuid(), documentNo: z.string().nullable(), status: z.string(), total: z.number().int(), subtotal: z.number().int().default(0), issueDate: z.string(), dueDate: z.string().nullable() }),
  ),
  /** 受注して、まだ請求書が1枚も無いか（「請求書をつくる」を出す） */
  canCreateInvoice: z.boolean(),
  /** 直している下書き（次の回）があるか（一覧のチップと同じ） */
  hasDraftRevision: z.boolean(),
  /** その下書きが何回目か（035。チップ「3回目 下書き」）。無ければ null */
  draftRevisionNo: z.number().int().nullable(),
  /** 分割の予定（042、docs/30）。無ければ null＝一括で請求する見積 */
  plan: BillingPlan.nullable().default(null),
  /** 直せる回のふつうの行の数（048、#82。0 なら外注見積を当てる相手が無い） */
  lineCount: z.number().int().default(0),
});
export type GroupJob = z.infer<typeof GroupJob>;

export const GroupSummary = z.object({
  jobCount: z.number().int(),
  draftCount: z.number().int(),
  ordered: z.object({ count: z.number().int(), total: z.number().int() }),
  billed: z.object({ count: z.number().int(), total: z.number().int() }),
  waiting: z.object({ count: z.number().int(), total: z.number().int() }),
  lost: z.object({ count: z.number().int(), total: z.number().int() }),
  /** 利益（受注した分、税抜）。ホームの月の数字と同じ形（ルール7） */
  profit: z.object({ amount: z.number().int().nullable(), rate: z.number().nullable(), missing: z.number().int() }),
  /** 未入金（E1。sales の会社だけ。/home と同じ式＝v_invoice_balance）。sales が OFF の会社は省く */
  unpaid: UnpaidFigures.optional(),
  /** 仕入と粗利（#233 3a。sales の会社だけ。v_project_margin）。sales が OFF の会社は省く＝今までの「利益」の枠 */
  margin: GroupMargin.optional(),
});
export type GroupSummary = z.infer<typeof GroupSummary>;

/* ---------------- 現場の地図（#132、docs/49 §2・§3） ---------------- */

/** 座標の粗さ：号まで／地番／街区／町字／手で直した／取れなかった */
export const SiteGeoLevel = z.enum(["residence", "parcel", "block", "town", "manual", "none"]);
export type SiteGeoLevel = z.infer<typeof SiteGeoLevel>;
/** 画面の左下に小さく出す文言（docs/49 §1-5。手で直したピンは何も出さない＝10/4 射場さん） */
export const SITE_GEO_LEVEL_LABEL: Record<SiteGeoLevel, string> = {
  residence: "住所から（番地まで）",
  parcel: "住所から（地番まで）",
  block: "住所から（街区まで）。ピンを修正してください",
  town: "町名まで。ピンを修正してください",
  manual: "",
  none: "座標を取れませんでした。ピンを置いてください",
};
/** 日本の範囲（緯度 20〜46・経度 122〜154）。ピンはこの中だけ */
export const JAPAN_BOUNDS = { minLat: 20, maxLat: 46, minLng: 122, maxLng: 154 } as const;
const latZ = z.number().min(JAPAN_BOUNDS.minLat, GROUP_MSG.geoOutOfJapan).max(JAPAN_BOUNDS.maxLat, GROUP_MSG.geoOutOfJapan);
const lngZ = z.number().min(JAPAN_BOUNDS.minLng, GROUP_MSG.geoOutOfJapan).max(JAPAN_BOUNDS.maxLng, GROUP_MSG.geoOutOfJapan);
/** 案件の座標（site_geo_enc を復号したもの）。null＝まだ取っていない／取れなかった（そのときは siteGeoLevel が none） */
export const SiteGeo = z.object({
  lat: latZ,
  lng: lngZ,
  level: SiteGeoLevel,
  /** abr＝住所から（ABR）／manual＝ピンを手で直した */
  source: z.enum(["abr", "manual"]),
  geocodedAt: z.string().nullable(),
  adjustedAt: z.string().nullable(),
});
export type SiteGeo = z.infer<typeof SiteGeo>;
/** ピンを直す（PUT /groups/:id/site-geo） */
export const SiteGeoPin = z.object({ lat: latZ, lng: lngZ });
export type SiteGeoPin = z.infer<typeof SiteGeoPin>;
/**
 * ピンの位置の住所（POST /groups/:id/site-geo/reverse、#135、docs/49 §11）。abr-geocoder の /reverse で引いた「いちばん近い住所」。
 * address が null＝取れなかった（ジオコーダが無い・落ちている・近くに住所が無い）。distanceM はピンからその住所の代表点までの距離（m）
 */
export const SiteGeoReverse = z.object({
  address: z.string().nullable(),
  level: SiteGeoLevel.nullable(),
  distanceM: z.number().nullable(),
  /** ピンの町丁目（#136、「兵庫県尼崎市浜田町2丁目」。境界データに無ければ null） */
  town: z.string().nullable().default(null),
  /** true＝その町に住所の点が無く町名まで（地番の位置が公開されていない町）。番地は手で足してもらう */
  townOnly: z.boolean().default(false),
});
export type SiteGeoReverse = z.infer<typeof SiteGeoReverse>;
/** 地図の種類（地理院タイル）：標準／淡色／航空写真 */
export const SiteMapStyle = z.enum(["std", "pale", "photo"]);
export type SiteMapStyle = z.infer<typeof SiteMapStyle>;
export const SITE_MAP_STYLE_LABEL: Record<SiteMapStyle, string> = { std: "標準", pale: "淡色", photo: "航空写真" };

export const GroupDetail = z.object({
  id: z.string().uuid(),
  name: z.string(),
  client: z.object({ id: z.string().uuid(), name: z.string(), honorific: z.string() }),
  siteAddress: z.string().nullable(),
  ownerName: z.string().nullable(),
  /** 現場の座標（#132）。null＝まだ取っていない／取れなかった */
  siteGeo: SiteGeo.nullable().default(null),
  /** 座標の粗さ（平文の列）。null＝まだ取っていない */
  siteGeoLevel: SiteGeoLevel.nullable().default(null),
  /* ---- 039（docs/28）---- */
  branch: z.object({ id: z.string().uuid(), name: z.string() }).nullable(),
  contact: z.object({ id: z.string().uuid(), name: z.string(), department: z.string() }).nullable(),
  staffName: z.string(),
  paymentTerms: GroupTerms,
  /** 社内メモ（暗号化。紙には出ない） */
  memo: z.string(),
  status: GroupStatus,
  completedAt: z.string().nullable(),
  nameFollowsSubject: z.boolean(),
  /** 請求タイミング（042、docs/30）。一括／分割 */
  billingTiming: BillingTiming,
  /** 継続案件（#289）と開始日。月別の表は GET /groups/:id/monthly */
  isOngoing: z.boolean().default(false),
  startedOn: z.string().nullable().default(null),
  /** 請求の形の上書き（#267）。null＝{customer}の設定に従う。billingModeOfClient＝{customer}の設定（「{customer}の設定（締め）」の表示） */
  billingMode: BillingMode.nullable().default(null),
  billingModeOfClient: BillingMode.default("per_delivery"),
  jobs: z.array(GroupJob),
  summary: GroupSummary,
  /** 「入金」タブ（E1。この案件の請求書に配分された入金。sales の会社だけ）。sales が OFF の会社は省く */
  payments: z.array(GroupPaymentRow).optional(),
  /** 工程表の要約（#100、docs/39 §3.7。タブ「工程」）。工程表が無ければ null。営業力の会社は省く */
  schedule: ScheduleBrief.nullable().optional(),
  /** 版（#202）：project_groups.updated_at。PUT /groups/:id に version として送り返す（社内メモの追記は版を見ない） */
  updatedAt: z.string(),
  /**
   * ゴミ箱にあるこの{project}の見積書（#265、docs/81 §12）。count＝件数、numbers＝そのうち番号のある見積書の番号。
   * 番号が1つでもあれば{project}は削除できない。番号なしだけなら、削除するとゴミ箱の見積書も完全に削除される
   */
  trash: z.object({ count: z.number().int(), numbers: z.array(z.string()) }).default({ count: 0, numbers: [] }),
});
export type GroupDetail = z.infer<typeof GroupDetail>;

/** 案件を直す。省いた欄は変えない。名前を直すと、以後は工事名に合わせない */
export const GroupUpdate = z.object({
  name: z.string().trim().min(1, GROUP_MSG.nameRequired).max(200, GROUP_MSG.nameTooLong).optional(),
  siteAddress: z.string().trim().max(200).optional(),
  ownerName: z.string().trim().max(100).optional(),
  partnerBranchId: z.string().uuid().nullable().optional(),
  partnerContactId: z.string().uuid().nullable().optional(),
  staffName: z.string().trim().max(50, GROUP_MSG.staffTooLong).optional(),
  /** null で「顧客に従う」に戻す */
  paymentTerms: PaymentTerms.nullable().optional(),
  memo: z.string().trim().max(10000, GROUP_MSG.memoTooLong).optional(),
  /** 請求タイミング（042）。分割にすると、受注した見積ごとに分割の予定を作れる */
  billingTiming: BillingTiming.optional(),
  /** 請求の形の上書き（#267）。null で「{customer}の設定に従う」に戻す */
  billingMode: BillingMode.nullable().optional(),
  /**
   * 現場の地図（#132、docs/49 §2.3）：住所を変えたとき、手で直したピンを新しい住所に合わせるか。
   * 省けば true。false なら手で直したピンはそのまま（住所から置いたピンは、住所が変われば必ず取り直す）
   */
  siteGeoFollow: z.boolean().optional(),
  /** 継続⇄通常（#289。書類の数に関わらずいつでも＝★A）と開始日（null で外す） */
  isOngoing: z.boolean().optional(),
  startedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  /** 版（#202）：画面が読んだときの GroupDetail.updatedAt。渡すと、いまの版と違えば 409 GROUP_STALE（{project}の画面は必ず送る） */
  version: Version.optional(),
});
export type GroupUpdate = z.infer<typeof GroupUpdate>;
